import React, { useEffect, useState } from 'react';
import { MdCheckCircle } from 'react-icons/md';
import PropTypes from 'prop-types';
import { checkEmailPost, verifyEmailOtpPost } from '../../../api-client/api-svcpp-client';
import { CustomButton, CustomFormInput } from '../../atoms';
import { generateRandomMobileNumber, hasDollarInString, isSuccessStatus, showErrorToast } from '../../../utils/utils';
import classes from './CustomFormEmail.module.scss';

const CustomFormEmail = (props) => {
    const {
        emailInput,
        studentDetails,
        isOtpVerificationEnabled,
        formik,
        isOtpVerificationSuccess,
        setIsOtpVerificationSuccess,
        isMultiVerEnabled,
        handleChangeVerificationMethod,
        maskedEmailString,
        isDisabled,
        duplicateMobileNumber,
        setDuplicateMobileNumber,
        customerNumber,
        formBlockData,
        // Primary identifier props
        isPrimaryIdentifierFlow,
        primaryIdentifierValue,
    } = props;

    if (!emailInput) return null;

    const [isOtpSent, setIsOtpSent] = useState(false);
    const [isSendOtpLoading, setIsSendOtpLoading] = useState(false);
    const [isVerifyOtpLoading, setIsVerifyOtpLoading] = useState(false);
    const [otpFieldError, setOtpFieldError] = useState('');
    const [otpFieldValue, setOtpFieldValue] = useState('');

    const handleSendOtpPost = async () => {
        try {
            // Get email from primary identifier or customer_email field
            const emailId = isPrimaryIdentifierFlow ? 
                primaryIdentifierValue : 
                formik?.values?.customer_email;

            const payload = {
                mobile_number: studentDetails?.customer_mobile || customerNumber,
                email_id: emailId,
            };
            setIsSendOtpLoading(true);
            const checkEmailPostRes = await checkEmailPost(payload);
            if (isSuccessStatus(checkEmailPostRes?.data)) {
                if (!studentDetails?.customer_mobile) {
                    setDuplicateMobileNumber(customerNumber);
                }
                setIsOtpSent(true);
            }
        } catch (error) {
            console.error('Error in handleSendOtpPost:', error);
            showErrorToast('Unable to send OTP, please contact support@grayquest.com');
        } finally {
            setIsSendOtpLoading(false);
        }
    };

    const handleSendOtpClick = async () => {
        // Get email from primary identifier or customer_email field
        const email = isPrimaryIdentifierFlow ? 
            primaryIdentifierValue : 
            formik?.values?.customer_email;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
        // Step 1: If OTP verification is enabled and email doesn't match student details
        if (
            isOtpVerificationEnabled &&
            studentDetails?.cust_bd?.email &&
            email !== studentDetails?.cust_bd?.email &&
            !isPrimaryIdentifierFlow
        ) {
            await formik?.setFieldTouched('customer_email', true);
            await formik?.setFieldError('customer_email', 'Email does not match');
            return;
        }
    
        // Step 2: For primary identifier flow, use the primary identifier value
        if (isPrimaryIdentifierFlow) {
            if (!email || !emailRegex.test(email)) {
                await formik?.setFieldTouched('stud_bd_student_uuid', true);
                await formik?.setFieldError('stud_bd_student_uuid', 'Please enter a valid email');
                return;
            }
        } else {
            // Step 2: If email is empty or has Formik error
            if (!email || formik.errors.customer_email) {
                formik.setFieldTouched('customer_email', true);
                return;
            }
        
            // Step 3: Validate email format
            if (!emailRegex.test(email)) {
                await formik.setFieldTouched('customer_email', true);
                await formik.setFieldError('customer_email', 'Enter a valid email');
                return;
            }
        
            // Step 4: All good, send OTP
            formik.setFieldTouched('customer_email', true);
        }
        
        await handleSendOtpPost();
    };
    

    const handleVerifyOtp = async (payload) => {
        try {
            setIsVerifyOtpLoading(true);
            const verifyEmailOtpPostRes = await verifyEmailOtpPost(payload);
            if (isSuccessStatus(verifyEmailOtpPostRes?.data)) {
                setIsOtpVerificationSuccess(true);
                if (
                    studentDetails?.customer_mobile &&
                    studentDetails?.customer_mobile?.length === 10 &&
                    !hasDollarInString(studentDetails?.customer_mobile)
                ) {
                    formik.setFieldValue('customer_mobile', studentDetails?.customer_mobile);
                }
            } else {
                setIsVerifyOtpLoading(false);
            }
        } catch (error) {
            console.error(error);
            if (error?.response?.data?.message) {
                showErrorToast(error?.response?.data?.message);
            } else {
                showErrorToast('Unable to verify OTP, please contact support@grayquest.com');
            }
            setIsVerifyOtpLoading(false);
        }
    };

    const handleVerifyOtpClick = async () => {
        setOtpFieldError('');
        if (!otpFieldValue || otpFieldValue?.length < 4) {
            setOtpFieldError('Please enter 4 digit OTP');
            return;
        }

        let isNum = /^\d+$/.test(otpFieldValue);
        if (!isNum) {
            setOtpFieldError('OTP must contain only numbers');
            return;
        }

        // Get email from primary identifier or customer_email field
        const emailId = isPrimaryIdentifierFlow ? 
            primaryIdentifierValue : 
            formik?.values?.customer_email;

        await handleVerifyOtp({
            mobile_number: studentDetails?.customer_mobile || duplicateMobileNumber,
            email_id: emailId,
            otp: otpFieldValue,
        });
    };

    const handleChangeVerfMethod = () => {
        handleChangeVerificationMethod('mobile_verf');
    };

    const handleOtpInputChange = (e) => {
        setOtpFieldError('');
        setOtpFieldValue(e?.target?.value || '');
    };

    // Auto-trigger OTP for primary identifier flow
    useEffect(() => {
        if (isPrimaryIdentifierFlow && isOtpVerificationEnabled && !isOtpSent && !isOtpVerificationSuccess) {
            // Check if we should block unverified students
            const blockUnverifiedStudent = formBlockData?.form_config?.block_unverified_student ?? false;
            const isActiveStudent = studentDetails?.stud_bd?.status === 'ACTIVE' || false;
            
            // If block unverified student is enabled, only trigger OTP for verified students
            if (blockUnverifiedStudent) {
                if (studentDetails && isActiveStudent) {
                    handleSendOtpClick();
                }
            } else {
                // If not blocking unverified students, trigger OTP
                handleSendOtpClick();
            }
        }
    }, [isPrimaryIdentifierFlow, isOtpVerificationEnabled, isOtpSent, isOtpVerificationSuccess, studentDetails, formBlockData]);

    return (
        <>
            {/* Only render input field if NOT in primary identifier flow */}
            {!isPrimaryIdentifierFlow && (
                <div className="row">
                    <div className="col-md-12">
                        <div className={classes.MobileInputWrap}>
                            <CustomFormInput
                                inputData={emailInput || null}
                                formik={formik}
                                key="emailInput"
                                isDisabled={(isOtpVerificationEnabled && isOtpSent) || isDisabled}
                                helperText={maskedEmailString}
                            />

                            {isMultiVerEnabled && !isOtpVerificationSuccess && (
                                <button
                                    type="button"
                                    onClick={handleChangeVerfMethod}
                                    className={classes.VerToggleButton}
                                    title="Click here to use mobile number for verification"
                                >
                                    <input type="checkbox" checked onChange={handleChangeVerfMethod} />
                                    <span>Want to verify via Email?</span>
                                </button>
                            )}

                            {isOtpVerificationSuccess ? (
                                <>
                                    <span className={classes.Text}>
                                        <span className={classes.Icon}>
                                            <MdCheckCircle fontSize="1.25rem" />
                                        </span>
                                        Email verified successfully!
                                    </span>
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            )}

            {/* Show success message for primary identifier flow */}
            {isPrimaryIdentifierFlow && isOtpVerificationSuccess && (
                <div className="row">
                    <div className="col-md-12">
                        <div className={classes.MobileInputWrap}>
                            <span className={classes.Text}>
                                <span className={classes.Icon}>
                                    <MdCheckCircle fontSize="1.25rem" />
                                </span>
                                Email verified successfully!
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {isOtpVerificationEnabled && !isOtpVerificationSuccess && (
                <>
                    {isOtpSent ? (
                        <>
                            <div className="row">
                                <div className="col-md-12">
                                    <div className={classes.OtpInputWrap}>
                                        <label htmlFor="customer_email_otp">Enter OTP</label>
                                        <input
                                            type="text"
                                            className={classes.OtpInput}
                                            placeholder="Enter OTP"
                                            id="customer_email_otp"
                                            name="customer_email_otp"
                                            onChange={handleOtpInputChange}
                                            disabled={!isOtpSent || isVerifyOtpLoading}
                                            maxLength={4}
                                        />
                                        {otpFieldError ? <span className="form-error">{otpFieldError}</span> : null}
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-12">
                                    <div className={classes.OtpButtonWrap}>
                                        <CustomButton
                                            type="button"
                                            text="Verify OTP"
                                            className={classes.PaymentForm__Submit}
                                            onClick={handleVerifyOtpClick}
                                            isLoading={isVerifyOtpLoading}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="row">
                            <div className="col-md-12">
                                <div className={classes.OtpButtonWrap}>
                                    <CustomButton
                                        type="button"
                                        text="Send OTP"
                                        variant="outline"
                                        className={classes.PaymentForm__Submit}
                                        onClick={handleSendOtpClick}
                                        isLoading={isSendOtpLoading}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

CustomFormEmail.propTypes = {
    emailInput: PropTypes.object,
    studentDetails: PropTypes.object,
    isOtpVerificationEnabled: PropTypes.bool,
    formik: PropTypes.object,
    isOtpVerificationSuccess: PropTypes.bool,
    setIsOtpVerificationSuccess: PropTypes.func,
    isMultiVerEnabled: PropTypes.bool,
    handleChangeVerificationMethod: PropTypes.func,
    maskedEmailString: PropTypes.string,
    isDisabled: PropTypes.bool,
    duplicateMobileNumber: PropTypes.string,
    setDuplicateMobileNumber: PropTypes.func,
    customerNumber: PropTypes.string,
    formBlockData: PropTypes.object,
    isPrimaryIdentifierFlow: PropTypes.bool,
    primaryIdentifierValue: PropTypes.string,
};

export default CustomFormEmail;
